import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  OnDestroy,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GestureEvent } from '@app/finxone-web-frontend/app/lib/gestures/gesture-event.interface';
import { GestureModule } from '@app/finxone-web-frontend/app/lib/gestures/gesture.module';

@Component({
  selector: 'finxone-web-frontend-pan-reveal',
  imports: [CommonModule, GestureModule],
  templateUrl: './pan-reveal.component.html',
  styleUrl: './pan-reveal.component.scss',
})
export class PanRevealComponent implements AfterViewInit, OnDestroy {
  @Input() threshold = 0.4;
  @Input() disabled = false;
  // reveal widths in pixels computed from the action containers (left and right)
  private leftRevealWidthPx = 0;
  private rightRevealWidthPx = 0;

  // small safe fudge to avoid off-by-a-few-pixels visual clipping
  private readonly fudgePx = 2;

  @ViewChild('root', { static: true }) private root!: ElementRef<HTMLElement>;
  @ViewChild('leftActions', { static: true }) private leftActions!: ElementRef<HTMLElement>;
  @ViewChild('rightActions', { static: true }) private rightActions!: ElementRef<HTMLElement>;

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private startX = 0;
  private currentX = 0;
  // open can be 'left' | 'right' | false
  private open: 'left' | 'right' | false = false;

  transform = 'translateX(0px)';

  // --- new fields for observation & scheduling ---
  private mutationObserver?: MutationObserver;
  private resizeObserver?: ResizeObserver;
  private measureDebounceTimer: any;
  private destroyed = false;

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.updateRevealWidth();

    // set up observers to detect when Handlebars/async content changes children or sizes
    // run observers outside Angular to avoid triggering change detection repeatedly
    this.ngZone.runOutsideAngular(() => {
      try {
        this.mutationObserver = new MutationObserver(() => {
          this.scheduleMeasure();
        });
        // observe child additions/character changes in case Handlebars injects HTML
        if (this.leftActions?.nativeElement) {
          this.mutationObserver.observe(this.leftActions.nativeElement, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
        if (this.rightActions?.nativeElement) {
          this.mutationObserver.observe(this.rightActions.nativeElement, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      } catch (e) {
        // ignore if MutationObserver is not available
      }

      try {
        // ResizeObserver can catch size changes (e.g. fonts or images loaded)
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        if (this.leftActions?.nativeElement) this.resizeObserver.observe(this.leftActions.nativeElement);
        if (this.rightActions?.nativeElement) this.resizeObserver.observe(this.rightActions.nativeElement);
      } catch (e) {
        // ignore if ResizeObserver is not available
      }
    });
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateRevealWidth();
  }

  // schedule a debounced measurement; called from observers (outside zone)
  private scheduleMeasure() {
    if (this.destroyed) return;
    clearTimeout(this.measureDebounceTimer);
    // small debounce to coalesce multiple mutations
    this.measureDebounceTimer = setTimeout(() => {
      // run the async measurement inside ngZone so updates propagate
      this.ngZone.run(() => {
        this.measureUntilStableAndUpdate();
      });
    }, 40);
  }

  // repeatedly check scrollWidth across animation frames until stable or max attempts reached
  private async measureUntilStableAndUpdate() {
    const maxAttempts = 6;
    let prevLeft = -1;
    let prevRight = -1;

    for (let i = 0; i < maxAttempts; i++) {
      if (!this.leftActions || !this.rightActions) break;
      const leftScroll = this.leftActions.nativeElement.scrollWidth || 0;
      const rightScroll = this.rightActions.nativeElement.scrollWidth || 0;

      // if both stable and we've seen a non-zero size (or reached last attempt), break
      if (leftScroll === prevLeft && rightScroll === prevRight) {
        if (leftScroll > 0 || rightScroll > 0 || i === maxAttempts - 1) break;
      }

      prevLeft = leftScroll;
      prevRight = rightScroll;

      // wait one animation frame for rendering to settle, then try again
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }

    // set widths based on final measurement
    this.updateRevealWidth();
  }

  private updateRevealWidth() {
    if (!this.root || !this.root.nativeElement) return;
    // measure the actual intrinsic widths of the action containers using scrollWidth (robust for flex children)
    const leftScroll = this.leftActions?.nativeElement?.scrollWidth || 0;
    const rightScroll = this.rightActions?.nativeElement?.scrollWidth || 0;

    this.leftRevealWidthPx = leftScroll ? Math.ceil(leftScroll) + this.fudgePx : 0;
    this.rightRevealWidthPx = rightScroll ? Math.ceil(rightScroll) + this.fudgePx : 0;

    // If currently open, ensure transform reflects the new pixel width for the open side
    if (this.open === 'left') {
      this.currentX = this.leftRevealWidthPx;
      this.transform = `translateX(${this.currentX}px)`;
    } else if (this.open === 'right') {
      this.currentX = -this.rightRevealWidthPx;
      this.transform = `translateX(${this.currentX}px)`;
    } else {
      this.currentX = 0;
      this.transform = `translateX(0px)`;
    }
  }

  public onPanStart() {
    if (this.disabled) return;
    this.startX = this.currentX;
  }

  public onPanMove(e: GestureEvent) {
    if (this.disabled) return;

    const delta = this.startX + e.deltaX;
    // clamp between -rightRevealWidthPx and +leftRevealWidthPx
    this.currentX = Math.max(-this.rightRevealWidthPx, Math.min(this.leftRevealWidthPx, delta));
    this.transform = `translateX(${this.currentX}px)`;
  }

  public onPanEnd(e: GestureEvent) {
    if (this.disabled) return;

    // decide whether to open left or right based on position and velocity
    const absX = Math.abs(this.currentX);

    // choose appropriate reveal width for the direction of the end position
    const candidateRevealWidth = this.currentX < 0 ? this.rightRevealWidthPx : this.leftRevealWidthPx;
    const openThreshold = candidateRevealWidth * this.threshold;

    const shouldOpenRight =
      this.currentX < 0 && candidateRevealWidth > 0 && (absX > openThreshold || e.velocityX < -0.3);
    const shouldOpenLeft =
      this.currentX > 0 && candidateRevealWidth > 0 && (absX > openThreshold || e.velocityX > 0.3);

    // If already open, a panEnd toggles closed (keep previous UX)
    if (this.open) {
      this.close();
      return;
    }

    if (shouldOpenLeft) {
      this.openLeft();
    } else if (shouldOpenRight) {
      this.openRight();
    } else {
      this.close();
    }
  }

  private openLeft() {
    if (this.leftRevealWidthPx <= 0) return;
    this.open = 'left';
    this.currentX = this.leftRevealWidthPx;
    this.transform = `translateX(${this.currentX}px)`;
    this.opened.emit();
  }

  private openRight() {
    if (this.rightRevealWidthPx <= 0) return;
    this.open = 'right';
    this.currentX = -this.rightRevealWidthPx;
    this.transform = `translateX(${this.currentX}px)`;
    this.opened.emit();
  }

  private close() {
    this.open = false;
    this.currentX = 0;
    this.transform = `translateX(0px)`;
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.measureDebounceTimer);
    if (this.mutationObserver) {
      try {
        this.mutationObserver.disconnect();
      } catch (e) {}
      this.mutationObserver = undefined;
    }
    if (this.resizeObserver) {
      try {
        this.resizeObserver.disconnect();
      } catch (e) {}
      this.resizeObserver = undefined;
    }
  }
}
